import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, JSX, Prop } from '@stencil/core';
import { getDataOnFromTheme, WorkbenchTheme } from '../../shared/themes';
import { RoundButtonElevation, RoundButtonSize } from './round-button.types';

// Components
import { TimerAnimation } from './timer-animation/timer-animation';

/**
 * Circular shape buttons with icon that behaves same as regular buttons.
 *
 * @slot - Mandatory label and wb-icon placed within the element
 */
@Component({
  tag: 'wb-round-button',
  styleUrl: './round-button.scss',
  shadow: true,
})
export class RoundButtonComponent implements ComponentInterface {
  @Element()
  public el: HTMLElement;

  /**
   * The colour scheme
   * @deprecated Use `data-on` attribute instead
   */
  @Prop({ reflect: true })
  public theme?: WorkbenchTheme;

  /** Size of the round button */
  @Prop({ reflect: true })
  public size!: RoundButtonSize;

  /** Elevation size consisting of border and shadow. `'m'` and `'l'` not available for dark theme */
  @Prop({ reflect: true })
  public elevation?: RoundButtonElevation;

  /** Duration in milliseconds for timer animation */
  @Prop({ reflect: true })
  public duration?: number;

  /** button-aria-label should be provided for accessibility reasons and it will be used in the correspondent element */
  @Prop({ reflect: true })
  public buttonAriaLabel!: string;

  /** Shows the button as disabled and prevents interactions */
  @Prop({ reflect: true })
  public disabled: boolean = false;

  /** Allow focus when it's disabled */
  @Prop({ reflect: true })
  public focusableWhenDisabled: boolean = false;

  /** Renders an anchor tag linked to the passed in URL (fragment) */
  @Prop()
  public href: string | undefined;

  /** Can be used in combination with `href` property */
  @Prop()
  public download: string | undefined;

  /** Can be used in combination with `href` property */
  @Prop()
  public rel: string | undefined;

  /** Can be used in combination with `href` property */
  @Prop()
  public target: string | undefined;

  /** Emitted when the button receives focus */
  @Event()
  public wbfocus!: EventEmitter<void>;

  /** Emitted when the button loses focus */
  @Event()
  public wbblur!: EventEmitter<void>;

  private onFocus = () => this.wbfocus.emit();

  private onBlur = () => this.wbblur.emit();

  public render(): JSX.Element {
    const shouldDisabledBeFocusable = this.disabled && this.focusableWhenDisabled;
    const shouldDisabledNotBeFocusable = this.disabled && !this.focusableWhenDisabled;

    return (
      <Host //
        class="wb-round-button"
        data-on={getDataOnFromTheme(this.el, this.theme)}
      >
        {this.href ? (
          <a //
            class="button"
            onFocus={this.onFocus}
            onBlur={this.onBlur}
            href={this.href}
            rel={this.rel}
            target={this.target}
            download={this.download}
            aria-label={this.buttonAriaLabel}
          >
            <slot />
            {this.duration && <TimerAnimation duration={this.duration} />}
          </a>
        ) : (
          <button //
            class="button"
            onFocus={this.onFocus}
            onBlur={this.onBlur}
            type="button"
            disabled={shouldDisabledNotBeFocusable}
            aria-disabled={shouldDisabledBeFocusable ? 'true' : 'false'}
            tabindex={shouldDisabledBeFocusable ? '0' : undefined}
            aria-label={this.buttonAriaLabel}
          >
            <slot />
            {this.duration && <TimerAnimation duration={this.duration} />}
          </button>
        )}
      </Host>
    );
  }
}
